import { Contract } from '@ethersproject/contracts'
import TickLensJson from '@uniswap/v3-periphery/artifacts/contracts/lens/TickLens.sol/TickLens.json'
import UniswapInterfaceMulticallJson from '@uniswap/v3-periphery/artifacts/contracts/lens/UniswapInterfaceMulticall.sol/UniswapInterfaceMulticall.json'
import ARGENT_WALLET_DETECTOR_ABI from 'abis/argent-wallet-detector.json'
import EIP_2612 from 'abis/eip_2612.json'
import ENS_PUBLIC_RESOLVER_ABI from 'abis/ens-public-resolver.json'
import ENS_ABI from 'abis/ens-registrar.json'
import ERC20_ABI from 'abis/erc20.json'
import ERC20_BYTES32_ABI from 'abis/erc20_bytes32.json'
import ERC721_ABI from 'abis/erc721.json'
import ERC1155_ABI from 'abis/erc1155.json'
import FAUCETABI from 'abis/Faucet.json'
import HypervisorABI from 'abis/Hypervisor.json'
import LockABI from 'abis/lock.json'
import MasterchefV3ABI from 'abis/MasterchefV3.json'
import minterABI from 'abis/Minter.json'
import NftPositionManagerABIs from 'abis/NftPositionManager.json'
import QUOTERABI from 'abis/quoter.json'
import tradeReaderABI from 'abis/Reader.json'
import RewardsDistributorABI from 'abis/RewardsDistributor.json'
import RoguFactoryABI from 'abis/RoguFactory.json'
import ROXUTILSABI from 'abis/RoguUtils.json'
import RoxPosnPoolABI from 'abis/RoxPosnPool.json'
import swapMinningABI from 'abis/swapMinning.json'
import TradePoolABI from 'abis/tradePool.json'
import TradeRouterABI from 'abis/TradeRouter.json'
import {
  ArgentWalletDetector,
  EnsPublicResolver,
  EnsRegistrar,
  Erc20,
  Erc721,
  Erc1155,
  Faucet,
  Hypervisor,
  Lock,
  MasterchefV3,
  Minter,
  NftPositionManager,
  PerpOrderbook,
  Reader,
  RewardsDistributor,
  RoguFactory,
  RoguUtils,
  RoxPosnPool,
  SwapMinning,
  TradePool,
  TradeRouter,
  UniswapV3Pool,
  Voter,
  VotingReward,
  Weth,
} from 'abis/types'
import V3POOLABI from 'abis/UniswapV3Pool.json'
import voterABI from 'abis/voter.json'
import votingRewardABI from 'abis/votingReward.json'
import WETH_ABI from 'abis/weth.json'
import { useActiveChainId, useEthersSigner } from 'connection/useActiveChainId'
import {
  ARGENT_WALLET_DETECTOR_ADDRESS,
  ENS_REGISTRAR_ADDRESSES,
  FAUCET_ADDRESSES,
  LOCK_ADDRESSES,
  MINTER_ADDRESSES,
  MULTICALL_ADDRESSES,
  NONFUNGIBLE_POSITION_MANAGER_ADDRESSES,
  QUOTER_ADDRESSES,
  REWARDS_DISTRIBUTOR_ADDRESSES,
  ROX_UTILS_ADDRESSES,
  TICK_LENS_ADDRESSES,
  TRADE_ROUTER_ADDRESSES,
  TRADEREADER_ADDRESSES,
  V3_CORE_FACTORY_ADDRESSES,
  VOTER_ADDRESSES,
} from 'constants/addresses'
import { getOrderBookContract } from 'constants/contractHelpers'
import { WRAPPED_NATIVE_CURRENCY } from 'constants/tokens'
import { useMemo } from 'react'
import { Quoter, TickLens, UniswapInterfaceMulticall } from 'types/v3'
import { getContract } from 'utils'

import { useProviderOrSigner, useProviderOrSigner2 } from './useProviderOrSigner'

const { abi: TickLensABI } = TickLensJson
const { abi: MulticallABI } = UniswapInterfaceMulticallJson
const { abi: NFTPositionManagerABI } = NftPositionManagerABIs

// returns null on errors
export function useContract<T extends Contract = Contract>(
  addressOrAddressMap: string | { [chainId: number]: string } | undefined,
  ABI: any,
  withSignerIfPossible = true
): T | null {
  const { chainId } = useActiveChainId()

  const provider = useProviderOrSigner(withSignerIfPossible)

  return useMemo(() => {
    if (!addressOrAddressMap || !ABI || !provider || !chainId) return null
    let address: string | undefined
    if (typeof addressOrAddressMap === 'string') address = addressOrAddressMap
    else address = addressOrAddressMap[chainId]
    if (!address) return null
    try {
      return getContract(address, ABI, provider)
    } catch (error) {
      console.error('Failed to get contract', error)
      return null
    }
  }, [addressOrAddressMap, ABI, chainId, provider]) as T
}

function useContract2<T extends Contract = Contract>(
  addressOrAddressMap: string | { [chainId: number]: string } | undefined,
  ABI: any,
  withSignerIfPossible = true
): T | null {
  const { chainId } = useActiveChainId()

  const provider = useProviderOrSigner2(true)

  return useMemo(() => {
    if (!addressOrAddressMap || !ABI || !provider || !chainId) return null
    let address: string | undefined
    if (typeof addressOrAddressMap === 'string') address = addressOrAddressMap
    else address = addressOrAddressMap[chainId]
    if (!address) return null
    try {
      return getContract(address, ABI, provider)
    } catch (error) {
      console.error('Failed to get contract', error)
      return null
    }
  }, [addressOrAddressMap, ABI, chainId, provider]) as T
}

export function useTokenContract(tokenAddress?: string, withSignerIfPossible?: boolean) {
  return useContract<Erc20>(tokenAddress, ERC20_ABI, withSignerIfPossible)
}

export function useWETHContract(withSignerIfPossible?: boolean) {
  const { chainId } = useActiveChainId()
  return useContract<Weth>(
    chainId ? WRAPPED_NATIVE_CURRENCY[chainId]?.address : undefined,
    WETH_ABI,
    withSignerIfPossible
  )
}

export function useERC721Contract(nftAddress?: string) {
  return useContract<Erc721>(nftAddress, ERC721_ABI, false)
}

export function useERC1155Contract(nftAddress?: string) {
  return useContract<Erc1155>(nftAddress, ERC1155_ABI, false)
}

export function useArgentWalletDetectorContract() {
  return useContract<ArgentWalletDetector>(ARGENT_WALLET_DETECTOR_ADDRESS, ARGENT_WALLET_DETECTOR_ABI, false)
}

export function useENSRegistrarContract(withSignerIfPossible?: boolean) {
  return useContract<EnsRegistrar>(ENS_REGISTRAR_ADDRESSES, ENS_ABI, withSignerIfPossible)
}

export function useENSResolverContract(address: string | undefined, withSignerIfPossible?: boolean) {
  return useContract<EnsPublicResolver>(address, ENS_PUBLIC_RESOLVER_ABI, withSignerIfPossible)
}

export function useBytes32TokenContract(tokenAddress?: string, withSignerIfPossible?: boolean): Contract | null {
  return useContract(tokenAddress, ERC20_BYTES32_ABI, withSignerIfPossible)
}

export function useEIP2612Contract(tokenAddress?: string): Contract | null {
  return useContract(tokenAddress, EIP_2612, false)
}

export function useInterfaceMulticall() {
  return useContract<UniswapInterfaceMulticall>(MULTICALL_ADDRESSES, MulticallABI, false) as UniswapInterfaceMulticall
}

export function useV3NFTPositionManagerContract(withSignerIfPossible?: boolean): NftPositionManager | null {
  return useContract<NftPositionManager>(
    NONFUNGIBLE_POSITION_MANAGER_ADDRESSES,
    NFTPositionManagerABI,
    withSignerIfPossible
  )
}

export function useV3Factory() {
  return useContract<RoguFactory>(V3_CORE_FACTORY_ADDRESSES, RoguFactoryABI.abi)
}

export function useQuoter() {
  return useContract<Quoter>(QUOTER_ADDRESSES, QUOTERABI.abi)
}

export function useTickLens(): TickLens | null {
  const { chainId } = useActiveChainId()
  const address = chainId ? TICK_LENS_ADDRESSES[chainId] : undefined
  return useContract(address, TickLensABI) as TickLens | null
}

export function useLockContract() {
  return useContract<Lock>(LOCK_ADDRESSES, LockABI.abi)
}

export function useTradePool(pool?: string) {
  return useContract<TradePool>(pool, TradePoolABI.abi)
}

export function useTradeRouter() {
  return useContract2<TradeRouter>(TRADE_ROUTER_ADDRESSES, TradeRouterABI.abi)
}

export function useVoter() {
  return useContract<Voter>(VOTER_ADDRESSES, voterABI.abi)
}

export function useMinter() {
  return useContract<Minter>(MINTER_ADDRESSES, minterABI.abi)
}

export function useMasterChefV3(address?: string) {
  return useContract<MasterchefV3>(address, MasterchefV3ABI.abi)
}

export function useSwapMinning(address?: string) {
  return useContract<SwapMinning>(address, swapMinningABI.abi)
}

export function useVotingReward(address?: string) {
  return useContract<VotingReward>(address, votingRewardABI.abi)
}

export function useHypervisor(address?: string) {
  return useContract<Hypervisor>(address, HypervisorABI.abi)
}
export function useFAUCET() {
  return useContract2<Faucet>(FAUCET_ADDRESSES, FAUCETABI)
}

export function useTradeReader() {
  return useContract<Reader>(TRADEREADER_ADDRESSES, tradeReaderABI.abi)
}
export function useRewardsDistributor() {
  return useContract<RewardsDistributor>(REWARDS_DISTRIBUTOR_ADDRESSES, RewardsDistributorABI.abi)
}
export function useV3PoolContract(pool?: string) {
  return useContract<UniswapV3Pool>(pool, V3POOLABI.abi, false)
}

export function useRoxUtils() {
  return useContract<RoguUtils>(ROX_UTILS_ADDRESSES, ROXUTILSABI.abi)
}

export function useOrderBookContract(): PerpOrderbook | undefined {
  const { chainId } = useActiveChainId()
  const singer = useEthersSigner()
  return getOrderBookContract(chainId, singer)
}

export function useRoxPosnPool(address?: string) {
  return useContract<RoxPosnPool>(address, RoxPosnPoolABI.abi, false)
}
